import type { ComponentProps, ReactNode } from 'react'
import { race, registrationPhase } from '../../shared/race.config'
import { formatClock, formatMoney } from '../../shared/format'
import type { Fundraising, PublicStats } from '../../shared/api-types'
import { ButtonLink } from './ui'

/**
 * The Register call to action, worded for where registration stands. It always leads to
 * /register, which (until registration opens) takes emails for the "it's open" message.
 */
export function RegisterButton({ children, soon = 'Get notified', ...rest }: Omit<ComponentProps<typeof ButtonLink>, 'to'> & { soon?: ReactNode }) {
  const phase = registrationPhase()
  return (
    <ButtonLink to="/register" {...rest}>
      {phase === 'open' ? children : phase === 'upcoming' ? soon : 'Registration closed'}
    </ButtonLink>
  )
}

/** Money raised for the cause against the goal. One series, so no legend. */
export function FundraisingMeter({ f, tone = 'light', className = '' }: { f: Fundraising; tone?: 'light' | 'dark'; className?: string }) {
  const pct = f.goalCents ? Math.min(100, (f.raisedCents / f.goalCents) * 100) : 0
  const dark = tone === 'dark'
  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <p className={dark ? 'text-white' : 'text-ink'}>
        <span className="font-display text-5xl font-extrabold leading-none">{formatMoney(f.raisedCents)}</span>{' '}
        <span className={dark ? 'text-white/75' : 'text-ink-2'}>raised for suicide prevention</span>
      </p>
      <div
        className={`h-3 overflow-hidden rounded-full ${dark ? 'bg-white/15' : 'bg-brand-soft'}`}
        role="meter"
        aria-valuemin={0}
        aria-valuemax={f.goalCents}
        aria-valuenow={f.raisedCents}
        aria-label="Raised so far against the fundraising goal"
      >
        <div className={`h-full rounded-full ${dark ? 'bg-accent' : 'bg-brand'}`} style={{ width: `${Math.max(1.5, pct)}%` }} />
      </div>
      <p className={`flex flex-wrap justify-between gap-x-4 text-sm num ${dark ? 'text-white/75' : 'text-ink-2'}`}>
        <span>
          {Math.round(pct)}% of the {formatMoney(f.goalCents)} goal
        </span>
        <span>
          {f.donors.toLocaleString('en-US')} {f.donors === 1 ? 'donor' : 'donors'}
        </span>
      </p>
    </div>
  )
}

export function CapacityMeter({ stats }: { stats: PublicStats }) {
  const pct = Math.min(100, Math.round((stats.registered / stats.capacity) * 100))
  const tone = pct >= 90 ? 'bg-danger' : pct >= 75 ? 'bg-warn' : 'bg-brand'
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-ink">
          <span className="font-display text-4xl font-extrabold">{stats.registered.toLocaleString('en-US')}</span>{' '}
          <span className="text-ink-2">registered</span>
        </p>
        <p className="text-sm text-ink-2 num">
          {(stats.capacity - stats.registered).toLocaleString('en-US')} of {stats.capacity.toLocaleString('en-US')} spots left
        </p>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-brand-soft" role="meter" aria-valuemin={0} aria-valuemax={stats.capacity} aria-valuenow={stats.registered} aria-label="Spots filled">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.max(2, pct)}%` }} />
      </div>
    </div>
  )
}

export function Schedule({ compact = false }: { compact?: boolean }) {
  const start = race.events[0].startTime
  const items = compact ? race.schedule.filter((s, i) => i === 0 || s.time === start || s.title.includes(race.memorial.name)) : race.schedule
  return (
    <ol className="relative flex flex-col">
      {items.map((s) => {
        const key = s.time === start || s.title.includes(race.memorial.name)
        return (
          <li key={s.time} className="grid grid-cols-[5.5rem_1fr] gap-4 pb-5 last:pb-0">
            <span className="pt-0.5 text-right font-display text-2xl font-bold leading-none text-brand num">{formatClock(s.time).replace(' ', ' ')}</span>
            <div className="relative border-l-2 border-line pl-5">
              <span className={`absolute -left-[7px] top-1.5 size-3 rounded-full border-2 border-surface ${key ? 'bg-accent' : 'bg-brand'}`} aria-hidden />
              <p className="font-semibold leading-snug">{s.title}</p>
              {!compact && <p className="text-[0.95rem] text-ink-2">{s.detail}</p>}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
